import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  Phone,
  Plus,
  Search,
  Star,
  Trash2,
  Wrench,
  X,
} from "lucide-react";
import { useState } from "react";
import { api } from "../lib/api";
import { usePermissoes } from "../lib/permissoes";

interface Props {
  condominioId: number;
  onVoltar: () => void;
  somenteLeitura?: boolean;   // Portal: só leitura, mesmo para o síndico (gestão é no painel)
}

interface Profissional {
  id: number;
  nome: string;
  especialidade: string;        // texto cru (1+ separadas por vírgula)
  especialidades: string[];     // já em lista
  telefone: string;
  whatsapp: string | null;
  email: string | null;
  atende_24h: boolean;
  cidade_regiao: string | null;
  indicado_por: string | null;
  avaliacao: number | null;
  observacoes: string | null;
  ativo: boolean;
  criado_em: string | null;
}

interface ProfissionaisResp {
  condominio_id: number;
  itens: Profissional[];
  especialidades: string[];
  contagem_por_especialidade: Record<string, number>;
  total: number;
}

// Formulário (criar/editar). Strings para os campos de texto; avaliação como número.
interface FormState {
  id?: number;
  nome: string;
  especialidades: string[];
  telefone: string;
  whatsapp: string;
  email: string;
  atende_24h: boolean;
  cidade_regiao: string;
  indicado_por: string;
  avaliacao: number | null;
  observacoes: string;
}

const FORM_VAZIO: FormState = {
  nome: "",
  especialidades: [],
  telefone: "",
  whatsapp: "",
  email: "",
  atende_24h: false,
  cidade_regiao: "",
  indicado_por: "",
  avaliacao: null,
  observacoes: "",
};

const soDigitos = (s: string) => (s || "").replace(/\D/g, "");
const waLink = (n: string) => `https://wa.me/55${soDigitos(n)}`;

export function ProfissionaisPage({ condominioId, onVoltar, somenteLeitura }: Props) {
  const { podeEscrever } = usePermissoes();
  const podeEditar = !somenteLeitura && podeEscrever(condominioId, "profissionais");
  const qc = useQueryClient();
  const [busca, setBusca] = useState("");
  const [filtroEsp, setFiltroEsp] = useState("");
  const [so24h, setSo24h] = useState(false);
  const [form, setForm] = useState<FormState | null>(null); // null = form fechado
  const [espInput, setEspInput] = useState(""); // texto do campo "adicionar especialidade"
  const [msg, setMsg] = useState<string | null>(null);
  // Cartões expandidos (colapsados por padrão: mostra só o nome; clica p/ detalhes).
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const alternar = (chave: string) =>
    setAbertos((s) => {
      const n = new Set(s);
      n.has(chave) ? n.delete(chave) : n.add(chave);
      return n;
    });

  // Adiciona/remove uma especialidade no formulário (sem duplicar).
  const addEsp = (val: string) => {
    const v = val.trim();
    if (!v) return;
    setForm((f) =>
      !f || f.especialidades.some((e) => e.toLowerCase() === v.toLowerCase())
        ? f
        : { ...f, especialidades: [...f.especialidades, v] },
    );
    setEspInput("");
  };
  const removeEsp = (val: string) =>
    setForm((f) => (f ? { ...f, especialidades: f.especialidades.filter((e) => e !== val) } : f));

  const { data, isLoading } = useQuery<ProfissionaisResp>({
    queryKey: ["profissionais", condominioId, busca, filtroEsp, so24h],
    queryFn: () => {
      const p = new URLSearchParams();
      if (busca.trim()) p.set("busca", busca.trim());
      if (filtroEsp) p.set("especialidade", filtroEsp);
      if (so24h) p.set("so_24h", "true");
      const qs = p.toString();
      return api.get<ProfissionaisResp>(
        `/condominios/${condominioId}/profissionais${qs ? `?${qs}` : ""}`,
      );
    },
  });

  const invalidar = () =>
    qc.invalidateQueries({ queryKey: ["profissionais", condominioId] });

  const salvar = useMutation({
    mutationFn: (f: FormState) => {
      const corpo = {
        nome: f.nome.trim(),
        especialidades: f.especialidades,
        telefone: f.telefone.trim(),
        whatsapp: f.whatsapp.trim() || null,
        email: f.email.trim() || null,
        atende_24h: f.atende_24h,
        cidade_regiao: f.cidade_regiao.trim() || null,
        indicado_por: f.indicado_por.trim() || null,
        avaliacao: f.avaliacao,
        observacoes: f.observacoes.trim() || null,
      };
      return f.id
        ? api.patch(`/profissionais/${f.id}`, corpo)
        : api.post(`/condominios/${condominioId}/profissionais`, corpo);
    },
    onSuccess: () => {
      setForm(null);
      setMsg("Profissional salvo.");
      invalidar();
    },
    onError: (e) => setMsg(e instanceof Error ? e.message : "Não foi possível salvar."),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => api.delete(`/profissionais/${id}`),
    onSuccess: () => invalidar(),
    onError: (e) => setMsg(e instanceof Error ? e.message : "Não foi possível excluir."),
  });

  const abrirNovo = () => {
    setMsg(null);
    setEspInput("");
    setForm({ ...FORM_VAZIO, especialidades: filtroEsp ? [filtroEsp] : [] });
  };
  const abrirEditar = (p: Profissional) => {
    setMsg(null);
    setEspInput("");
    setForm({
      id: p.id,
      nome: p.nome,
      especialidades: p.especialidades ?? [],
      telefone: p.telefone,
      whatsapp: p.whatsapp ?? "",
      email: p.email ?? "",
      atende_24h: p.atende_24h,
      cidade_regiao: p.cidade_regiao ?? "",
      indicado_por: p.indicado_por ?? "",
      avaliacao: p.avaliacao,
      observacoes: p.observacoes ?? "",
    });
  };
  const confirmarExcluir = (p: Profissional) => {
    if (window.confirm(`Excluir "${p.nome}" (${p.especialidade})? Não pode ser desfeito.`)) {
      excluir.mutate(p.id);
    }
  };

  const formOk =
    form && form.nome.trim() && form.especialidades.length > 0 && form.telefone.trim();

  // Agrupa por especialidade — um profissional com VÁRIAS aparece em cada grupo.
  // MAS: se há um FILTRO por especialidade, o profissional aparece só no grupo
  // daquela especialidade (não nas outras dele) — senão poluía a lista pedida.
  const grupos: { especialidade: string; itens: Profissional[] }[] = [];
  for (const p of data?.itens ?? []) {
    let esps = p.especialidades.length ? p.especialidades : ["(sem especialidade)"];
    if (filtroEsp) {
      const so = esps.filter((e) => e.toLowerCase() === filtroEsp.toLowerCase());
      esps = so.length ? so : [filtroEsp]; // garante que ele apareça no grupo pedido
    }
    for (const esp of esps) {
      let g = grupos.find((x) => x.especialidade === esp);
      if (!g) {
        g = { especialidade: esp, itens: [] };
        grupos.push(g);
      }
      g.itens.push(p);
    }
  }
  grupos.sort((a, b) => a.especialidade.localeCompare(b.especialidade, "pt-BR"));

  // Sugestões ainda não escolhidas no formulário (para os chips de "+ adicionar").
  const sugestoesNaoUsadas = (data?.especialidades ?? []).filter(
    (e) => !form?.especialidades.some((x) => x.toLowerCase() === e.toLowerCase()),
  );

  return (
    <div>
      <button
        type="button"
        onClick={onVoltar}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft size={16} /> Voltar
      </button>

      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Wrench size={22} className="text-brand-700" />
          <h2 className="text-2xl font-semibold text-slate-900">Profissionais</h2>
        </div>
        {podeEditar && (
          <button
            type="button"
            onClick={abrirNovo}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-700 hover:bg-brand-800 text-white text-sm font-medium rounded-md"
          >
            <Plus size={16} /> Novo profissional
          </button>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Agenda de prestadores por especialidade — hidráulico, eletricista, pintor,
        empresas de serviço. Marque quem atende <b>24h</b> para achar rápido numa
        emergência.
      </p>

      {/* Busca + filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar nome, telefone, região…"
            className="w-64 pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-md"
          />
        </div>
        <select
          value={filtroEsp}
          onChange={(e) => setFiltroEsp(e.target.value)}
          className="px-2 py-2 text-sm border border-slate-300 rounded-md bg-white"
        >
          <option value="">Todas as especialidades</option>
          {(data?.especialidades ?? []).map((e) => (
            <option key={e} value={e}>
              {e}
              {data?.contagem_por_especialidade?.[e]
                ? ` (${data.contagem_por_especialidade[e]})`
                : ""}
            </option>
          ))}
        </select>
        <label className="inline-flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={so24h} onChange={(e) => setSo24h(e.target.checked)} />
          Só 24h
        </label>
      </div>

      {msg && (
        <div className="mb-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
          {msg}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 size={16} className="animate-spin" /> Carregando…
        </div>
      ) : (data?.total ?? 0) === 0 ? (
        <div className="p-6 text-center text-slate-500 text-sm border border-dashed border-slate-300 rounded-xl">
          Nenhum profissional {busca || filtroEsp || so24h ? "com esse filtro" : "cadastrado"} ainda.
          Clique em <b>Novo profissional</b> para começar.
        </div>
      ) : (
        <div className="space-y-5">
          {grupos.map((g) => (
            <div key={g.especialidade}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                {g.especialidade} · {g.itens.length}
              </h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {g.itens.map((p) => {
                  const chave = `${g.especialidade}-${p.id}`;
                  const aberto = abertos.has(chave);
                  return (
                  <div key={chave} className="bg-white border border-slate-200 rounded-xl shadow-sm">
                    {/* Linha colapsada: só o nome (+ selos), clicável para abrir */}
                    <button
                      type="button"
                      onClick={() => alternar(chave)}
                      aria-expanded={aberto}
                      className="w-full flex items-center gap-2 p-3 text-left"
                    >
                      {aberto
                        ? <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />
                        : <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />}
                      <span className="font-medium text-slate-900 truncate">{p.nome}</span>
                      {p.atende_24h && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 flex-shrink-0">
                          24h
                        </span>
                      )}
                      {p.avaliacao ? (
                        <span className="inline-flex items-center gap-0.5 text-[11px] text-amber-600 flex-shrink-0">
                          <Star size={12} className="fill-amber-400 text-amber-400" />
                          {p.avaliacao}
                        </span>
                      ) : null}
                    </button>

                    {/* Detalhes: só quando expandido */}
                    {aberto && (
                      <div className="px-3 pb-3 flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          {p.especialidades.length > 1 && (
                            <div className="flex flex-wrap gap-1 mb-1">
                              {p.especialidades.map((e) => (
                                <span
                                  key={e}
                                  className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                                    e === g.especialidade
                                      ? "bg-brand-50 border-brand-200 text-brand-700 font-medium"
                                      : "bg-slate-50 border-slate-200 text-slate-500"
                                  }`}
                                >
                                  {e}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="flex flex-col gap-0.5 text-[13px] text-slate-600">
                            <a href={`tel:${soDigitos(p.telefone)}`} className="inline-flex items-center gap-1.5 hover:text-brand-700">
                              <Phone size={12} /> {p.telefone}
                            </a>
                            {p.whatsapp && (
                              <a href={waLink(p.whatsapp)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-emerald-700 hover:underline">
                                WhatsApp: {p.whatsapp}
                              </a>
                            )}
                            {p.email && (
                              <a href={`mailto:${p.email}`} className="hover:text-brand-700 truncate">
                                {p.email}
                              </a>
                            )}
                            {p.cidade_regiao && <span className="text-slate-500">{p.cidade_regiao}</span>}
                            {p.indicado_por && (
                              <span className="text-slate-400 text-[11px]">Indicado por: {p.indicado_por}</span>
                            )}
                            {p.observacoes && (
                              <span className="text-slate-500 text-[12px] whitespace-pre-wrap">{p.observacoes}</span>
                            )}
                          </div>
                        </div>
                        {podeEditar && (
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => abrirEditar(p)}
                              title="Editar"
                              className="p-1.5 text-slate-400 hover:text-slate-700 rounded"
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => confirmarExcluir(p)}
                              title="Excluir"
                              className="p-1.5 text-slate-400 hover:text-rose-600 rounded"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal criar/editar */}
      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                {form.id ? "Editar profissional" : "Novo profissional"}
              </h3>
              <button type="button" onClick={() => setForm(null)} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto grid grid-cols-2 gap-3">
              <label className="block col-span-2">
                <span className="block text-[11px] font-medium text-slate-500 mb-1">Nome *</span>
                <input
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Ex.: João Encanador ou HidroJá Ltda"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </label>
              <div className="block col-span-2">
                <span className="block text-[11px] font-medium text-slate-500 mb-1">
                  Especialidades * (uma ou mais — ideal para empresas)
                </span>
                {/* Chips já escolhidas */}
                {form.especialidades.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {form.especialidades.map((e) => (
                      <span
                        key={e}
                        className="inline-flex items-center gap-1 text-[12px] px-2 py-0.5 rounded-full bg-brand-100 text-brand-800"
                      >
                        {e}
                        <button type="button" onClick={() => removeEsp(e)} className="hover:text-rose-600" title="Remover">
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {/* Campo para adicionar (com sugestões) */}
                <div className="flex gap-1">
                  <input
                    list="especialidades-sugeridas"
                    value={espInput}
                    onChange={(e) => setEspInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addEsp(espInput);
                      }
                    }}
                    placeholder="Ex.: Hidráulica — digite ou escolha e clique em +"
                    className="flex-1 px-2 py-1.5 border border-slate-300 rounded-md text-sm"
                  />
                  <datalist id="especialidades-sugeridas">
                    {(data?.especialidades ?? []).map((e) => (
                      <option key={e} value={e} />
                    ))}
                  </datalist>
                  <button
                    type="button"
                    onClick={() => addEsp(espInput)}
                    className="px-3 py-1.5 text-sm rounded-md bg-slate-700 text-white hover:bg-slate-800"
                  >
                    +
                  </button>
                </div>
                {/* Sugestões rápidas para clicar */}
                {sugestoesNaoUsadas.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {sugestoesNaoUsadas.slice(0, 10).map((e) => (
                      <button
                        key={e}
                        type="button"
                        onClick={() => addEsp(e)}
                        className="text-[11px] px-2 py-0.5 rounded-full border border-slate-300 text-slate-600 hover:border-brand-400 hover:text-brand-700"
                      >
                        + {e}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <label className="block">
                <span className="block text-[11px] font-medium text-slate-500 mb-1">Telefone *</span>
                <input
                  value={form.telefone}
                  onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                  placeholder="(51) 99999-0000"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-slate-500 mb-1">WhatsApp</span>
                <input
                  value={form.whatsapp}
                  onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                  placeholder="(51) 99999-0000"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-slate-500 mb-1">E-mail</span>
                <input
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-slate-500 mb-1">Cidade/região</span>
                <input
                  value={form.cidade_regiao}
                  onChange={(e) => setForm({ ...form, cidade_regiao: e.target.value })}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-slate-500 mb-1">Indicado por</span>
                <input
                  value={form.indicado_por}
                  onChange={(e) => setForm({ ...form, indicado_por: e.target.value })}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-slate-500 mb-1">Avaliação</span>
                <select
                  value={form.avaliacao ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, avaliacao: e.target.value ? Number(e.target.value) : null })
                  }
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
                >
                  <option value="">—</option>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {"★".repeat(n)} ({n})
                    </option>
                  ))}
                </select>
              </label>
              <label className="col-span-2 inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.atende_24h}
                  onChange={(e) => setForm({ ...form, atende_24h: e.target.checked })}
                />
                Atende <b>24 horas</b> / emergência
              </label>
              <label className="block col-span-2">
                <span className="block text-[11px] font-medium text-slate-500 mb-1">Observações</span>
                <textarea
                  value={form.observacoes}
                  onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                  rows={3}
                  placeholder="Valores, condições, serviços já feitos…"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm resize-y"
                />
              </label>
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200">
              <button type="button" onClick={() => setForm(null)} className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900">
                Cancelar
              </button>
              <button
                type="button"
                disabled={!formOk || salvar.isPending}
                onClick={() => form && salvar.mutate(form)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white text-sm font-medium rounded-md"
              >
                {salvar.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
